import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { DragEvent as ReactDragEvent, MouseEvent } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CHAT_PAGE, chatApi } from "../../api/chat";
import type { ChatAttachment, ChatChannel, ChatMessage, ChatPerson } from "../../api/chat";
import { useAuth } from "../../auth/AuthContext";
import { useChat } from "../../components/chat/useChatConnection";
import { useConfirm } from "../../components/ConfirmDialog";
import { useToastError } from "../../components/Toast";
import Ic from "../../components/icons.jsx";
import { ChatAvatar } from "./ChatAvatar";
import { BrowseChannelsDialog, EditChannelDialog, MembersDialog, NewChannelDialog, NewDirectDialog } from "./ChatDialogs";
import { roleLabel } from "./chatPeople";
import { fold, layoutRows } from "./chatFormat";
import { MessageInput } from "./MessageInput";
import { Lightbox, MessageRow } from "./MessageRow";
import { SearchResults } from "./SearchResults";
import type { SearchQuery } from "./SearchResults";
import { ThreadPanel } from "./ThreadPanel";
import { ThreadsList, useUnreadThreads } from "./ThreadsList";
import { intlLocale } from "../../lib/format";

const LAST_CHANNEL_KEY = "bankom.chat.last";
const NARROW = "(max-width: 820px)";

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(NARROW).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(NARROW);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

function remember(id: string | null) {
  try {
    if (id) window.localStorage.setItem(LAST_CHANNEL_KEY, id);
    else window.localStorage.removeItem(LAST_CHANNEL_KEY);
  } catch {
    // Storage refused; the chat opens on the channel for everyone instead.
  }
}

function lastChannel(): string | null {
  try {
    return window.localStorage.getItem(LAST_CHANNEL_KEY);
  } catch {
    return null;
  }
}

/** Messages in time order, the given ones in place of any with the same id. */
function merge(list: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(list.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/**
 * The staff chat, Slack's way: channels on the left, the open channel on the right. On a phone the two take turns —
 * the list at /chat, a channel at /chat/:id with a way back. A message search (/chat?q=) and the threads the user
 * follows (/chat?view=threads) take the channel's place; what they list opens its channel at the message (?m=, and
 * &reply= for a reply in its thread).
 */
export default function ChatPage() {
  const { t } = useTranslation();
  const chat = useChat();
  const { channelId } = useParams<{ channelId?: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const searching = !channelId && params.has("q");
  const threadsView = !channelId && !searching && params.get("view") === "threads";
  const query = useMemo<SearchQuery>(
    () => ({ q: params.get("q") ?? "", channelId: params.get("in") ?? "", authorId: params.get("from") ?? "" }),
    [params],
  );
  const target = channelId ? params.get("m") : null;
  const reply = target ? params.get("reply") : null;
  const narrow = useNarrow();
  const [dialog, setDialog] = useState<null | "new" | "browse" | "direct">(null);
  const drafts = useRef(new Map<string, string>());
  const people = useMemo(
    () => [...chat.people.values()].sort((a, b) => a.name.localeCompare(b.name)),
    [chat.people],
  );
  const { channels, refreshPeople, upsertChannel } = chat;
  const current = channels?.find((c) => c.id === channelId) ?? null;

  // Fresh pictures and names each time the screen opens.
  useEffect(() => { void refreshPeople(); }, [refreshPeople]);

  // On a wide screen the chat always shows a channel, or a search: the last one opened, else the one for everyone.
  useEffect(() => {
    if (narrow || channelId || searching || threadsView || !channels || channels.length === 0) return;
    const last = lastChannel();
    const target = channels.find((c) => c.id === last) ?? channels.find((c) => c.defaultChannel) ?? channels[0];
    navigate(`/chat/${target.id}`, { replace: true });
  }, [narrow, channelId, searching, threadsView, channels, navigate]);

  // A channel in the address that is not there for this user (closed, left, never theirs).
  useEffect(() => {
    if (channelId && channels && !current) navigate("/chat", { replace: true });
  }, [channelId, channels, current, navigate]);

  useEffect(() => {
    if (current) remember(current.id);
  }, [current]);

  const join = useCallback(async (c: ChatChannel) => {
    upsertChannel(await chatApi.join(c.id));
  }, [upsertChannel]);

  // The conversation with one colleague, from the new-message dialog or a channel's member list.
  const openDirect = useCallback(async (personId: string) => {
    const c = await chatApi.direct(personId);
    upsertChannel(c);
    setDialog(null);
    navigate(`/chat/${c.id}`);
  }, [upsertChannel, navigate]);

  const search = useCallback((next: SearchQuery, replace: boolean) => {
    const to = new URLSearchParams({ q: next.q });
    if (next.channelId) to.set("in", next.channelId);
    if (next.authorId) to.set("from", next.authorId);
    navigate(`/chat?${to}`, { replace });
  }, [navigate]);

  const view = narrow ? (current || searching || threadsView ? "conversation" : "list") : "both";

  return (
    <div className="chat" data-view={view}>
      <ChatSidebar
        channels={channels}
        currentId={current?.id ?? null}
        threadsActive={threadsView}
        onThreads={() => navigate("/chat?view=threads")}
        onNew={() => setDialog("new")}
        onBrowse={() => setDialog("browse")}
        onNewDirect={() => setDialog("direct")}
        onSearch={(q) => search({ q, channelId: "", authorId: "" }, false)}
      />
      {searching ? (
        <SearchResults query={query} narrow={narrow} onChange={search} />
      ) : threadsView ? (
        <ThreadsList narrow={narrow} />
      ) : current ? (
        <Conversation
          // Opening the channel at another found message starts it afresh, there.
          key={`${current.id}:${target ?? ""}:${reply ?? ""}`}
          channel={current}
          target={target}
          reply={reply}
          people={people}
          narrow={narrow}
          draft={drafts.current.get(current.id)}
          onDraft={(d) => { drafts.current.set(current.id, d); }}
          onJoin={() => join(current)}
          onMessage={openDirect}
        />
      ) : (
        <section className="chat-main chat-placeholder">
          {channels === null ? (
            <p className="chat-muted">{t("chat.loading", "Loading…")}</p>
          ) : (
            <p className="chat-muted">{t("chat.pickChannel", "Pick a channel on the left.")}</p>
          )}
        </section>
      )}
      {dialog === "new" && (
        <NewChannelDialog
          people={people}
          me={chat.me}
          onClose={() => setDialog(null)}
          onCreated={(c) => {
            upsertChannel(c);
            setDialog(null);
            navigate(`/chat/${c.id}`);
          }}
        />
      )}
      {dialog === "direct" && (
        <NewDirectDialog
          people={people}
          me={chat.me}
          online={chat.online}
          onClose={() => setDialog(null)}
          onPick={openDirect}
        />
      )}
      {dialog === "browse" && channels && (
        <BrowseChannelsDialog
          channels={channels}
          onClose={() => setDialog(null)}
          onOpen={(c) => { setDialog(null); navigate(`/chat/${c.id}`); }}
          onJoin={join}
          onNew={() => setDialog("new")}
        />
      )}
    </div>
  );
}

function ChatSidebar({ channels, currentId, threadsActive, onThreads, onNew, onBrowse, onNewDirect, onSearch }: {
  channels: ChatChannel[] | null;
  currentId: string | null;
  /** The threads the user follows are on screen. */
  threadsActive: boolean;
  onThreads: () => void;
  onNew: () => void;
  onBrowse: () => void;
  onNewDirect: () => void;
  /** Searches the messages for what was typed; the box keeps finding channels by name as it is typed. */
  onSearch: (q: string) => void;
}) {
  const { t } = useTranslation();
  const { status, people, online } = useChat();
  const unreadThreads = useUnreadThreads();
  const [q, setQ] = useState("");
  // Slack's list: the channels one is in, plus the one being looked at without joining; under them the conversations
  // with colleagues, the latest first. The search finds a conversation by the colleague's name.
  const mine = (channels ?? []).filter((c) => c.member || c.id === currentId);
  const nameOf = (c: ChatChannel) => (c.directWith ? people.get(c.directWith)?.name ?? "" : c.name);
  const found = (c: ChatChannel) => !q || fold(nameOf(c)).includes(fold(q));
  const rooms = mine.filter((c) => !c.directWith && found(c));
  const directs = mine
    .filter((c) => c.directWith && found(c))
    .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));
  const toJoin = (channels ?? []).filter((c) => !c.member && !c.privateChannel).length;

  function searchMessages() {
    if (!q.trim()) return;
    onSearch(q.trim());
    setQ("");
  }

  return (
    <aside className="chat-side" aria-label={t("chat.channels", "Channels")}>
      <div className="chat-side-head">
        <h2>{t("chat.title", "Chat")}</h2>
        <button
          type="button"
          className="chat-tool"
          onClick={onNew}
          title={t("chat.newChannel", "New channel")}
          aria-label={t("chat.newChannel", "New channel")}
        >
          <Ic.plus />
        </button>
      </div>
      <form className="chat-filter" role="search" onSubmit={(e) => { e.preventDefault(); searchMessages(); }}>
        <Ic.search />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") setQ(""); }}
          placeholder={t("chat.searchBox", "Search channels and messages")}
          aria-label={t("chat.searchBox", "Search channels and messages")}
          enterKeyHint="search"
        />
      </form>
      <nav className="chat-side-list">
        {q.trim() && (
          <button type="button" className="chat-ch chat-ch-search" onClick={searchMessages}>
            <Ic.search className="chat-ch-icon" />
            <span className="chat-ch-name">{t("chat.searchMessagesFor", "Search messages for “{{q}}”", { q: q.trim() })}</span>
            <kbd aria-hidden="true">↵</kbd>
          </button>
        )}
        {!q && (
          <button
            type="button"
            className={`chat-ch chat-ch-threads${threadsActive ? " active" : ""}${unreadThreads > 0 ? " unread" : ""}`}
            aria-current={threadsActive ? "page" : undefined}
            onClick={onThreads}
          >
            <Ic.threads className="chat-ch-icon" />
            <span className="chat-ch-name">{t("chat.threads", "Threads")}</span>
            {unreadThreads > 0 && (
              <span className="chat-badge mention"
                aria-label={t("chat.threadsUnread", "{{count}} threads with new replies", { count: unreadThreads })}>
                {unreadThreads > 99 ? "99+" : unreadThreads}
              </span>
            )}
          </button>
        )}
        {/* While the box finds by name, a section with nothing found drops its heading. */}
        {(!q || rooms.length > 0) && <div className="chat-side-label">{t("chat.channels", "Channels")}</div>}
        {channels === null && [0, 1, 2].map((i) => <div key={i} className="chat-ch-skeleton" />)}
        {rooms.map((c) => <ChannelRow key={c.id} channel={c} active={c.id === currentId} />)}
        {!q && (
          <button type="button" className="chat-ch chat-ch-browse" onClick={onBrowse}>
            <Ic.compass className="chat-ch-icon" />
            <span className="chat-ch-name">{t("chat.browse", "Browse channels")}</span>
            {toJoin > 0 && <span className="chat-ch-more">{toJoin}</span>}
          </button>
        )}
        {(!q || directs.length > 0) && (
          <div className="chat-side-label with-action">
            <span>{t("chat.directs", "Direct messages")}</span>
            <button
              type="button"
              className="chat-tool sm"
              onClick={onNewDirect}
              title={t("chat.newDirect", "New direct message")}
              aria-label={t("chat.newDirect", "New direct message")}
            >
              <Ic.plus />
            </button>
          </div>
        )}
        {directs.map((c) => (
          <DirectRow
            key={c.id}
            channel={c}
            person={people.get(c.directWith!)}
            online={online.has(c.directWith!)}
            active={c.id === currentId}
          />
        ))}
        {!q && channels !== null && directs.length === 0 && (
          <button type="button" className="chat-ch chat-ch-browse" onClick={onNewDirect}>
            <Ic.user className="chat-ch-icon" />
            <span className="chat-ch-name">{t("chat.messageColleague", "Message a colleague")}</span>
          </button>
        )}
        {q && rooms.length === 0 && directs.length === 0 && (
          <p className="chat-muted chat-side-empty">{t("chat.noChannelsFound", "No channel by that name.")}</p>
        )}
      </nav>
      {(status === "reconnecting" || status === "connecting") && (
        <div className="chat-conn" role="status">
          <span className="chat-conn-dot" />
          {status === "connecting" ? t("chat.connecting", "Connecting…") : t("chat.reconnecting", "Reconnecting…")}
        </div>
      )}
    </aside>
  );
}

function ChannelRow({ channel, active }: { channel: ChatChannel; active: boolean }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const unread = channel.member && channel.unread > 0;
  return (
    <button
      type="button"
      className={`chat-ch${active ? " active" : ""}${unread ? " unread" : ""}${channel.member ? "" : " preview"}`}
      aria-current={active ? "page" : undefined}
      onClick={() => navigate(`/chat/${channel.id}`)}
    >
      {channel.privateChannel ? <Ic.lock className="chat-ch-icon" /> : <Ic.hash className="chat-ch-icon" />}
      <span className="chat-ch-name">{channel.name}</span>
      {channel.mentions > 0 ? (
        <span className="chat-badge mention" aria-label={t("chat.mentionsCount", "{{count}} mentions", { count: channel.mentions })}>
          {channel.mentions > 99 ? "99+" : channel.mentions}
        </span>
      ) : unread ? (
        <span className="chat-badge" aria-label={t("chat.unreadCount", "{{count}} unread", { count: channel.unread })}>
          {channel.unread > 99 ? "99+" : channel.unread}
        </span>
      ) : null}
    </button>
  );
}

/** A conversation with a colleague: their picture and name; every unread message in it is for the user, so red. */
function DirectRow({ channel, person, online, active }: {
  channel: ChatChannel;
  person: ChatPerson | undefined;
  online: boolean;
  active: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const unread = channel.unread > 0;
  return (
    <button
      type="button"
      className={`chat-ch chat-ch-direct${active ? " active" : ""}${unread ? " unread" : ""}`}
      aria-current={active ? "page" : undefined}
      onClick={() => navigate(`/chat/${channel.id}`)}
    >
      <ChatAvatar person={person} id={channel.directWith!} size="xs" online={online} />
      <span className="chat-ch-name">{person?.name ?? t("chat.unknownPerson", "former colleague")}</span>
      {unread && (
        <span className="chat-badge mention" aria-label={t("chat.unreadCount", "{{count}} unread", { count: channel.unread })}>
          {channel.unread > 99 ? "99+" : channel.unread}
        </span>
      )}
    </button>
  );
}

type Pending = { id: string; body: string; files: File[]; createdAt: string; failed: boolean };

function Conversation({ channel, target, reply, people, narrow, draft, onDraft, onJoin, onMessage }: {
  channel: ChatChannel;
  /** A message a search led to: the channel opens at it. With `reply`, its thread opens too, at that reply. */
  target: string | null;
  reply: string | null;
  people: ChatPerson[];
  narrow: boolean;
  draft: string | undefined;
  onDraft: (body: string) => void;
  onJoin: () => Promise<void>;
  onMessage: (personId: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const chat = useChat();
  const { me, subscribe, setOpenChannel, upsertChannel, removeChannel, refreshChannels, status } = chat;
  const peopleById = chat.people;
  const navigate = useNavigate();
  // Come from the search results, the way back leads to them.
  const fromSearch = Boolean((useLocation().state as { fromSearch?: boolean } | null)?.fromSearch);
  const confirm = useConfirm();
  const toastError = useToastError();
  // Anyone's message may be deleted by a moderator; the server checks the same right.
  const moderator = useAuth().hasPermission("chat:moderate");

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [palette, setPalette] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | "members" | "edit">(null);
  const [lightbox, setLightbox] = useState<ChatAttachment | null>(null);
  // The message whose thread is open beside the channel.
  const [thread, setThread] = useState<string | null>(reply ? target : null);
  // The message a search led to, lit up for a moment once it is in view.
  const [flash, setFlash] = useState<string | null>(null);
  const closeThread = useCallback(() => setThread(null), []);
  const [dragging, setDragging] = useState(false);
  const [incoming, setIncoming] = useState<{ id: number; files: File[] } | undefined>(undefined);
  const [newBelow, setNewBelow] = useState(0);
  const [faces, setFaces] = useState<string[]>([]);
  const [joining, setJoining] = useState(false);
  // Where "new messages" starts, taken once as the channel opens: reading moves the mark, not the line.
  const [newAfter] = useState(channel.member && channel.unread > 0 ? channel.lastReadAt : null);

  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const placed = useRef(false);
  const keepPosition = useRef<{ height: number; top: number } | null>(null);
  const readTimer = useRef<number | undefined>(undefined);

  const markRead = useCallback(() => {
    if (!channel.member) return;
    window.clearTimeout(readTimer.current);
    readTimer.current = window.setTimeout(() => {
      if (document.visibilityState === "visible") void chatApi.markRead(channel.id).catch(() => {});
    }, 500);
  }, [channel.id, channel.member]);

  useEffect(() => () => window.clearTimeout(readTimer.current), []);

  useEffect(() => {
    setOpenChannel(channel.id);
    return () => setOpenChannel(null);
  }, [channel.id, setOpenChannel]);

  useEffect(() => {
    let live = true;
    (target ? chatApi.messagesThrough(channel.id, target) : chatApi.messages(channel.id))
      .then((page) => {
        if (!live) return;
        setMessages([...page].reverse());
        // Opened at a found message, the page is longer than one; whether anything is older shows on scrolling up.
        setHasMore(page.length >= CHAT_PAGE);
        setLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setFailed(true);
        setLoading(false);
      });
    chatApi.members(channel.id).then((ids) => { if (live) setFaces(ids); }).catch(() => {});
    return () => { live = false; };
  }, [channel.id, target]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 3000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  // What was unread is read once it is on screen.
  useEffect(() => {
    if (!loading && channel.unread > 0) markRead();
  }, [loading, channel.unread, markRead]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible" && channel.unread > 0) markRead(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [channel.unread, markRead]);

  useEffect(() => subscribe((ev) => {
    // A reply belongs to its thread; the channel hears of it through its message's updated summary.
    if (ev.type === "message" && ev.message.channelId === channel.id && ev.message.parentId === null) {
      const m = ev.message;
      setMessages((list) => merge(list, [m]));
      if (m.authorId === me) {
        // The stream can beat the send's own answer; the message it brings replaces the one shown as sending.
        setPending((list) => {
          const i = list.findIndex((p) => p.body === m.body && !p.failed);
          return i < 0 ? list : [...list.slice(0, i), ...list.slice(i + 1)];
        });
      } else {
        markRead();
        if (!atBottom.current) setNewBelow((n) => n + 1);
      }
    } else if (ev.type === "message" && ev.message.channelId === channel.id && ev.message.authorId !== me
      && (ev.message.mentions.includes(me) || ev.message.followers.includes(me))) {
      // A reply for the user in one of the channel's threads is seen here, as the channel list counts it; the server
      // hears so too, or the thread would stay new in the list of threads and its ring in the bell.
      markRead();
    } else if (ev.type === "updated" && ev.message.channelId === channel.id) {
      setMessages((list) => list.map((m) => (m.id === ev.message.id ? ev.message : m)));
    } else if (ev.type === "deleted" && ev.channelId === channel.id) {
      setMessages((list) => list.filter((m) => m.id !== ev.messageId));
    } else if (ev.type === "ready") {
      // Back after a break in the stream: whatever was said meanwhile.
      chatApi.messages(channel.id).then((page) => setMessages((list) => merge(list, page))).catch(() => {});
    }
  }), [subscribe, channel.id, me, markRead]);

  // A reaction menu closes on a click anywhere else.
  useEffect(() => {
    if (!palette) return;
    const close = (e: Event) => {
      if (!(e.target as HTMLElement).closest(".chat-palette, .chat-palette-toggle")) setPalette(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [palette]);

  const shown = useMemo<ChatMessage[]>(() => [
    ...messages,
    ...pending.map((p) => ({
      id: p.id, channelId: channel.id, authorId: me, body: p.body, mentions: [], createdAt: p.createdAt,
      editedAt: null, reactions: [], attachments: [], parentId: null, parentAuthorId: null, replyCount: 0,
      lastReplyAt: null, replyUsers: [], followers: [],
    })),
  ], [messages, pending, channel.id, me]);

  const rows = useMemo(
    () => layoutRows(shown, {
      me, newAfter, now: new Date(), today: t("chat.today", "Today"), yesterday: t("chat.yesterday", "Yesterday"),
    }),
    [shown, me, newAfter, t],
  );

  // Scrolling: open at a found message, else the first new one (or the bottom); keep the place when older messages
  // come in above, and follow new ones only when already at the bottom.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || loading) return;
    if (keepPosition.current) {
      el.scrollTop = el.scrollHeight - keepPosition.current.height + keepPosition.current.top;
      keepPosition.current = null;
      return;
    }
    if (!placed.current) {
      placed.current = true;
      const row = target ? el.querySelector<HTMLElement>(`[data-message="${target}"]`) : null;
      if (row) {
        el.scrollTop += row.getBoundingClientRect().top - el.getBoundingClientRect().top - (el.clientHeight - row.offsetHeight) / 2;
        atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        // A found reply lights up in its thread; the message it answers only gives it its place.
        if (!reply) setFlash(target);
        // Little above it, nothing scrolls up to bring the older messages: the check a scroll makes runs once now.
        window.setTimeout(() => el.dispatchEvent(new Event("scroll")), 0);
        return;
      }
      // Too far back to open the channel at: the message shows in its thread, beside the newest.
      if (target && !reply) setThread(target);
      const line = el.querySelector<HTMLElement>(".chat-new");
      el.scrollTop = line && line.offsetTop < el.scrollHeight - el.clientHeight ? Math.max(0, line.offsetTop - 96) : el.scrollHeight;
      atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      return;
    }
    if (atBottom.current) el.scrollTop = el.scrollHeight;
  }, [rows, loading, target, reply]);

  async function loadOlder() {
    const oldest = messages[0];
    if (!oldest || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const page = await chatApi.messages(channel.id, oldest.id);
      const el = scroller.current;
      if (el) keepPosition.current = { height: el.scrollHeight, top: el.scrollTop };
      setMessages((list) => merge(list, page));
      setHasMore(page.length === CHAT_PAGE);
    } catch {
      // Scrolling up again tries again.
    } finally {
      setLoadingOlder(false);
    }
  }

  function onScroll() {
    const el = scroller.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (atBottom.current && newBelow > 0) setNewBelow(0);
    if (el.scrollTop < 160 && hasMore && !loadingOlder) void loadOlder();
  }

  // A picture takes its height only once it has loaded, after the list was put at the bottom: whoever was at the
  // bottom stays there.
  const keepBottom = useCallback(() => {
    const el = scroller.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, []);

  function toBottom() {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setNewBelow(0);
  }

  async function send(body: string, files: File[], retryOf?: string) {
    const p: Pending = { id: `sending-${Date.now()}-${Math.random().toString(36).slice(2)}`, body, files,
      createdAt: new Date().toISOString(), failed: false };
    atBottom.current = true;
    setPending((list) => [...list.filter((x) => x.id !== retryOf), p]);
    try {
      const m = files.length > 0
        ? await chatApi.postWithFiles(channel.id, body, files)
        : await chatApi.post(channel.id, body);
      setPending((list) => list.filter((x) => x.id !== p.id));
      setMessages((list) => merge(list, [m]));
      if (!channel.member) void refreshChannels();
    } catch {
      setPending((list) => list.map((x) => (x.id === p.id ? { ...x, failed: true } : x)));
    }
  }

  async function react(message: ChatMessage, emoji: string) {
    setPalette(null);
    try {
      const updated = await chatApi.react(message.id, emoji);
      setMessages((list) => list.map((m) => (m.id === updated.id ? updated : m)));
    } catch (e) {
      toastError(e);
    }
  }

  async function saveEdit(message: ChatMessage, body: string) {
    setEditing(null);
    if (body === message.body) return;
    try {
      const updated = await chatApi.edit(message.id, body);
      setMessages((list) => list.map((m) => (m.id === updated.id ? updated : m)));
    } catch (e) {
      toastError(e);
    }
  }

  async function remove(message: ChatMessage) {
    const ok = await confirm({
      title: t("chat.deleteTitle", "Delete this message?"),
      message: message.replyCount > 0
        ? t("chat.deleteWithThread", "It disappears for everyone in the channel, together with its thread.")
        : t("chat.deleteMessage", "It disappears for everyone in the channel."),
      confirmLabel: t("chat.delete", "Delete"),
      danger: true,
    });
    if (!ok) return;
    try {
      await chatApi.remove(message.id);
      setMessages((list) => list.filter((m) => m.id !== message.id));
      if (thread === message.id) setThread(null);
    } catch (e) {
      toastError(e);
    }
  }

  async function leave() {
    const ok = await confirm({
      title: t("chat.leaveTitle", "Leave #{{name}}?", { name: channel.name }),
      message: channel.privateChannel
        ? t("chat.leavePrivate", "The channel is private: to come back, a member has to add you again.")
        : t("chat.leavePublic", "You can join again any time from Browse channels."),
      confirmLabel: t("chat.leave", "Leave channel"),
      danger: channel.privateChannel,
    });
    if (!ok) return;
    try {
      await chatApi.leave(channel.id);
      remember(null);
      if (channel.privateChannel) removeChannel(channel.id);
      else upsertChannel({ ...channel, member: false, unread: 0, mentions: 0, memberCount: channel.memberCount - 1 });
      navigate("/chat");
    } catch (e) {
      toastError(e);
    }
  }

  function editLast() {
    const last = [...messages].reverse().find((m) => m.authorId === me);
    if (last) setEditing(last.id);
  }

  // Files dropped anywhere on the conversation go to the writing box; one dropped on the box itself the box takes.
  const carriesFiles = (e: ReactDragEvent<HTMLElement>) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  function onDragOver(e: ReactDragEvent<HTMLElement>) {
    if (!channel.member || !carriesFiles(e)) return;
    e.preventDefault();
    setDragging(true);
  }
  function onDragLeave(e: ReactDragEvent<HTMLElement>) {
    if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node | null)) setDragging(false);
  }
  function onDrop(e: ReactDragEvent<HTMLElement>) {
    setDragging(false);
    if (!channel.member || !carriesFiles(e) || e.defaultPrevented) return;
    e.preventDefault();
    const list = Array.from(e.dataTransfer.files);
    if (list.length > 0) setIncoming({ id: Date.now(), files: list });
  }

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLightbox(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]);

  function onRowClick(e: MouseEvent<HTMLDivElement>, id: string) {
    // On a touch screen there is no hover: a tap shows the message's tools.
    if ((e.target as HTMLElement).closest("button, a, textarea, input")) return;
    setSelected((s) => (s === id ? null : id));
  }

  const pendingById = new Map(pending.map((p) => [p.id, p]));
  const facePeople = [...faces]
    .sort((a, b) => Number(chat.online.has(b)) - Number(chat.online.has(a)))
    .slice(0, 3);
  const created = new Intl.DateTimeFormat(intlLocale(), { dateStyle: "long" });
  const canWrite = channel.member;
  // A conversation with a colleague reads as that colleague: picture, name and role instead of a channel's # and topic.
  const partnerId = channel.directWith;
  const partner = partnerId ? peopleById.get(partnerId) : undefined;
  const partnerOnline = partnerId ? chat.online.has(partnerId) : false;
  const title = partnerId ? partner?.name ?? t("chat.unknownPerson", "former colleague") : channel.name;

  return (
    <>
    {/* The conversation takes files dropped on it; picking them is the paper clip's job, from the keyboard too. */}
    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
    <section
      className={`chat-main${dragging ? " dragging" : ""}`}
      aria-label={partnerId ? title : `#${channel.name}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <header className="chat-head">
        {narrow && (
          <button type="button" className="chat-tool chat-back" onClick={() => (fromSearch ? navigate(-1) : navigate("/chat"))}
            aria-label={fromSearch ? t("chat.backToResults", "Back to the results") : t("chat.back", "Back to channels")}>
            <Ic.arrowLeft />
          </button>
        )}
        <div className="chat-head-title">
          {partnerId
            ? <ChatAvatar person={partner} id={partnerId} size="sm" online={partnerOnline} />
            : channel.privateChannel ? <Ic.lock /> : <Ic.hash />}
          <h1>{title}</h1>
        </div>
        {partnerId ? (
          <p className="chat-head-topic">
            {[partner ? roleLabel(partner, t) : "", partnerOnline ? t("chat.online", "online") : ""].filter(Boolean).join(" · ")}
          </p>
        ) : (
          channel.topic && <p className="chat-head-topic" title={channel.topic}>{channel.topic}</p>
        )}
        <div className="chat-head-actions">
          {!partnerId && (
          <button type="button" className="chat-faces" onClick={() => setDialog("members")}
            title={t("chat.members", "Members")} aria-label={t("chat.membersCountLabel", "{{count}} members", { count: channel.memberCount })}>
            <span className="chat-faces-stack">
              {facePeople.map((id) => <ChatAvatar key={id} person={peopleById.get(id)} id={id} size="xs" />)}
            </span>
            <span>{channel.memberCount}</span>
          </button>
          )}
          {channel.canManage && (
            <button type="button" className="chat-tool" onClick={() => setDialog("edit")}
              title={t("chat.editChannel", "Channel settings")} aria-label={t("chat.editChannel", "Channel settings")}>
              <Ic.pencil />
            </button>
          )}
          {channel.member && !channel.defaultChannel && !partnerId && (
            <button type="button" className="chat-tool" onClick={leave}
              title={t("chat.leave", "Leave channel")} aria-label={t("chat.leave", "Leave channel")}>
              <Ic.leave />
            </button>
          )}
        </div>
      </header>

      {status === "reconnecting" && (
        <div className="chat-banner" role="status">
          {t("chat.offline", "The connection dropped. New messages will arrive as soon as it is back.")}
        </div>
      )}

      <div className="chat-scroll-wrap">
        <div className="chat-scroll" ref={scroller} onScroll={onScroll}>
          {loading && (
            <div className="chat-loading" aria-busy="true">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="chat-msg head chat-msg-skeleton">
                  <div className="chat-msg-gutter"><span className="chat-av md chat-skel" /></div>
                  <div className="chat-msg-main"><span className="chat-skel line short" /><span className="chat-skel line" /></div>
                </div>
              ))}
            </div>
          )}
          {failed && <p className="chat-error chat-pad">{t("chat.loadFailed", "The messages could not be loaded.")}</p>}
          {!loading && !failed && !hasMore && partnerId && (
            <div className="chat-intro">
              <ChatAvatar person={partner} id={partnerId} size="xl" online={partnerOnline} />
              <h2>{title}</h2>
              <p>
                {partner && <span className="chat-intro-role">{roleLabel(partner, t)}</span>}
                {t("chat.directIntro", "This conversation is just between the two of you.")}
              </p>
            </div>
          )}
          {!loading && !failed && !hasMore && !partnerId && (
            <div className="chat-intro">
              <div className="chat-intro-mark">{channel.privateChannel ? <Ic.lock /> : <Ic.hash />}</div>
              <h2>
                {channel.defaultChannel
                  ? t("chat.introDefaultTitle", "Welcome to #{{name}}", { name: channel.name })
                  : t("chat.introTitle", "This is the start of #{{name}}", { name: channel.name })}
              </h2>
              <p>
                {channel.defaultChannel
                  ? t("chat.introDefault", "Everyone at Bankom is in this channel. Use it for news and for anything the whole company should hear.")
                  : channel.topic
                    ? channel.topic
                    : t("chat.introOpened", "Opened on {{date}}.", { date: created.format(new Date(channel.createdAt)) })}
              </p>
              {channel.member && !channel.defaultChannel && (
                <button type="button" className="btn sm" onClick={() => setDialog("members")}>
                  <Ic.users /> {t("chat.addPeople", "Add people")}
                </button>
              )}
            </div>
          )}
          {loadingOlder && <div className="chat-older">{t("chat.loadingOlder", "Loading earlier messages…")}</div>}
          {rows.map((row) => {
            if (row.kind === "day") return <div key={row.key} className="chat-day"><span>{row.label}</span></div>;
            if (row.kind === "new") return <div key={row.key} className="chat-new"><span>{t("chat.newMessages", "New messages")}</span></div>;
            const m = row.message;
            const p = pendingById.get(m.id);
            const mine = m.authorId === me;
            return (
              <MessageRow
                key={row.key}
                message={m}
                head={row.head}
                person={peopleById.get(m.authorId)}
                online={chat.online.has(m.authorId)}
                people={peopleById}
                me={me}
                mine={mine}
                canDelete={mine || moderator}
                sending={p ? (p.failed ? "failed" : "sending") : null}
                editing={editing === m.id}
                selected={selected === m.id}
                paletteOpen={palette === m.id}
                peopleList={people}
                onClick={(e) => onRowClick(e, m.id)}
                onReact={(emoji) => react(m, emoji)}
                onPalette={() => setPalette((x) => (x === m.id ? null : m.id))}
                onEdit={() => setEditing(m.id)}
                onCancelEdit={() => setEditing(null)}
                onSaveEdit={(body) => saveEdit(m, body)}
                onDelete={() => remove(m)}
                onRetry={() => p && send(p.body, p.files, p.id)}
                pendingFiles={p?.files}
                onOpenImage={setLightbox}
                onMediaLoad={keepBottom}
                onReplies={p ? undefined : () => setThread(m.id)}
                onDiscard={() => setPending((list) => list.filter((x) => x.id !== m.id))}
                found={flash === m.id}
              />
            );
          })}
        </div>
        {newBelow > 0 && (
          <button type="button" className="chat-jump" onClick={toBottom}>
            <Ic.arrowDown /> {t("chat.newBelow", "{{count}} new", { count: newBelow })}
          </button>
        )}
      </div>

      <div className="chat-compose">
        {canWrite ? (
          <>
            <MessageInput
              key={channel.id}
              variant="compose"
              people={people}
              placeholder={partnerId
                ? t("chat.directPlaceholder", "Message {{name}}", { name: title })
                : t("chat.placeholder", "Message #{{name}}", { name: channel.name })}
              initialBody={draft}
              onDraft={onDraft}
              onSubmit={(body, files) => void send(body, files)}
              incoming={incoming}
              onEditLast={editLast}
              focusOnOpen={!narrow}
            />
            {!narrow && (
              <p className="chat-hint">
                <span><kbd>Enter</kbd> {t("chat.hintSend", "sends")}</span>
                <span><kbd>Shift</kbd>+<kbd>Enter</kbd> {t("chat.hintNewLine", "new line")}</span>
                <span>{t("chat.hintFiles", "Drag or paste a picture to send it")}</span>
              </p>
            )}
          </>
        ) : (
          <div className="chat-joinbar">
            <span>
              <Ic.eye /> {t("chat.previewing", "You are looking at #{{name}} without being in it.", { name: channel.name })}
            </span>
            <button
              type="button"
              className="btn primary sm"
              disabled={joining}
              onClick={async () => {
                setJoining(true);
                try { await onJoin(); } catch (e) { toastError(e); } finally { setJoining(false); }
              }}
            >
              {t("chat.join", "Join")}
            </button>
          </div>
        )}
      </div>

      {dragging && (
        <div className="chat-drop" aria-hidden="true">
          <Ic.attach />
          <span>{t("chat.dropHere", "Drop the files to send them")}</span>
        </div>
      )}
      {lightbox && <Lightbox file={lightbox} onClose={() => setLightbox(null)} />}
      {dialog === "members" && (
        <MembersDialog
          channel={channel}
          people={people}
          online={chat.online}
          me={me}
          onClose={() => {
            setDialog(null);
            chatApi.members(channel.id).then(setFaces).catch(() => {});
          }}
          onMessage={(id) => {
            setDialog(null);
            onMessage(id).catch((e) => toastError(e));
          }}
        />
      )}
      {dialog === "edit" && (
        <EditChannelDialog
          channel={channel}
          onClose={() => setDialog(null)}
          onSaved={(c) => { upsertChannel(c); setDialog(null); }}
          onArchived={() => { setDialog(null); remember(null); removeChannel(channel.id); navigate("/chat"); }}
        />
      )}
    </section>
    {thread && (
      <ThreadPanel key={thread} channel={channel} parentId={thread} people={people} narrow={narrow} onClose={closeThread}
        found={thread === target ? reply : null} />
    )}
    </>
  );
}
